import { FC } from 'react';
import FormRow from '@components/primitives/form/FormRow';
import FormInput from '@components/primitives/form/FormInput';
import { Sizes } from '@utils/sizes';
import Button from '@components/primitives/button/Button';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import Heading from '@components/primitives/heading/Heading';
import { local } from '@utils/storage';
import { StorageKeys } from '@constants/storageKeys';
import { toast } from 'react-toastify';
import { buildVersion } from '@constants/buildVersion';
import { useMsal } from '@azure/msal-react';
import useAuthenticate from '@components/pages/loginPage/hooks/useAuthenticate';
import useAuthentication from '@hooks/useAuthentication';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useStartPage from '@components/features/startPageSwitch/hooks/useStartPage';
import { useAtom } from 'jotai';
import { redirectAfterLoginAtom } from '@js/store/authenticationAtoms';
import MaintenanceMessage from '@components/features/maintenanceMessage/MaintenanceMessage';
import RotatingTranslation from '@components/primitives/rotatingTranslation/RotatingTranslation';

type TenantFormType = {
    tenantId: string;
};

const useCreateSchema = (): yup.ObjectSchema<TenantFormType> => {
    const { t } = useTranslation();
    return yup.object().shape({
        tenantId: yup
            .string()
            .trim()
            .required(t('validation.tenantId.required', { ns: 'authentication' })),
    });
};

const Tenant: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tenantParam = searchParams.get('tenant');
    const { startPage } = useStartPage();
    const schema = useCreateSchema();
    const { accounts } = useMsal();
    const currentAccount = accounts[0];
    const { getAzureToken } = useAuthentication();
    const [redirectAfterLogin, setRedirectAfterLogin] = useAtom(redirectAfterLoginAtom);
    const { mutate: authenticate, isPending: isAuthenticating } = useAuthenticate();

    const methods = useForm({
        resolver: yupResolver(schema),
        mode: 'onTouched',
        defaultValues: {
            tenantId: tenantParam ? tenantParam : local.getItem(StorageKeys.TenantId) || '',
        },
    });

    const { handleSubmit } = methods;

    const onSubmit = async (data: TenantFormType) => {
        toast.dismiss();
        const azureToken = await getAzureToken();

        if (!azureToken) {
            console.warn('Missing access token');
            return;
        }

        local.setItem(StorageKeys.TenantId, data.tenantId);
        authenticate(
            {
                ...data,
                emailAddress: currentAccount.username,
                azureToken,
                clientVersion: buildVersion,
            },
            {
                onSuccess: () => {
                    if (!redirectAfterLogin || redirectAfterLogin === '/') {
                        startPage?.navigate();
                        return;
                    }
                    navigate(redirectAfterLogin);
                    setRedirectAfterLogin(undefined);
                },
            },
        );
    };

    return (
        <>
            <MaintenanceMessage />
            <Heading className="loginBox__heading" size={Sizes.xxl}>
                <RotatingTranslation ns="authentication" i18nKey="tenant.title" />
            </Heading>
            <p className="loginBox__text">
                <RotatingTranslation ns="authentication" i18nKey="tenant.text" />
            </p>
            <br />
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormRow name="tenantId">
                        <FormInput size={Sizes.xl} placeholder={t('login.tenantId', { ns: 'authentication' })} type="text" name="tenantId" testId="login-tenant-input" />
                    </FormRow>
                    <FormRow>
                        <Button type="submit" testId="login-submit-tenant" disabled={isAuthenticating} isLoading={isAuthenticating} primary size={Sizes.lg}>
                            <RotatingTranslation ns="authentication" i18nKey="login.button" />
                        </Button>
                    </FormRow>
                </form>
            </FormProvider>
        </>
    );
};

export default Tenant;
